import { storage } from '@/shared/helpers/storage';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { oauthRequest } from '@/shared/request-configs/oauth-request';

/**
 * Fetches Current User <br>
 * API Endpoint - `/auth/current-user` <br> via {@link adminRequest.get}
 */
export const getCurrent = async () => {
  const result = await adminRequest.get(`/auth/current-user`);
  return result.data ? result : null;
};

export type MfaMode = 'app' | 'email' | 'phone';

/**
 * Makes a request to login the user <br>
 * API Endpoint - `/public/user/login` <br> via {@link oauthRequest.post}
 * @param email - email of the user to login
 * @param password - password of the user to login
 * @param mfaToken - mfa token user may receive by email, text or from an authenticator app
 * @param mfaType - MFA method the token belongs to. Sent without a token, `email` asks for a new email code.
 */
export const login = async ({
  email,
  password,
  mfaToken,
  mfaType,
}: {
  email: string,
  password: string,
  mfaToken?: string,
  mfaType?: MfaMode,
}) => {
  const reqPayload = { email, passRaw: password, passHash: '', mfaToken, mfaType };
  const result = await oauthRequest.post('/public/user/login', reqPayload);
  storage.remove('currentUserSettings');
  storage.set('isNewSession', true);
  return result;
};

/**
 * Logs the user in with a single-use MFA recovery code <br>
 * API Endpoint - `/public/user/login/recover` <br> via {@link oauthRequest.post}
 * @param email - email of the user to login
 * @param password - password of the user to login
 * @param recoveryCode - one of the recovery codes given when the user set up an authenticator app
 */
export const loginWithRecoveryCode = async ({
  email,
  password,
  recoveryCode,
}: {
  email: string,
  password: string,
  recoveryCode: string,
}) => {
  const reqPayload = { email, passRaw: password, passHash: '', mfaRecovery: recoveryCode };
  const result = await oauthRequest.post('/public/user/login/recover', reqPayload);
  storage.remove('currentUserSettings');
  storage.set('isNewSession', true);
  return result;
};
/**
 * Makes a request to logout the user <br>
 * API Endpoint - '/auth/user/logout' <br> via {@link oauthRequest.post}
 */
export const logout = async () => {
  await oauthRequest.post('/auth/user/logout', {});

  storage.remove('currentUserSettings');

  if (window) {
    window.location.reload();
  }
};
/**
 * Resets the Password with the new one <br>
 * API Endpoint - `/public/user/forgot/confirm` <br> via {@link oauthRequest.post}
 * @param password - new password to reset
 * @param forgotCode - code req. to reset the password
 */
export const resetPassword = async ({ password, forgotCode }: { password: string, forgotCode: string }) => {
  const result = await oauthRequest.post('/public/user/forgot/confirm', { passHash: password, forgotId: forgotCode });
  return result;
};
/**
 * Fetches forgot code <br>
 * API Endpoint - `/public/user/forgot` <br> via {@link oauthRequest.get}
 * @param email
 */
export const forgotPassword = async (email: string) => {
  const result = await oauthRequest.post('/public/user/forgot', { email });
  return result;
};
/**
 * Fetches token <br>
 * API Endpoint - `auth/token/refresh` <br> via {@link oauthRequest.get}
 */
export const token = async () => {
  const result = await oauthRequest.post('/auth/token/refresh', {});
  return result;
};
/**
 * Impersonates current user to specified user <br>
 * API Endpoint - `/auth/token/refresh` <br> via {@link oauthRequest.post}
 * @param userId - id of the user to be impersonated
 */
export const impersonate = async (userId: number | string) => {
  const result = await oauthRequest.post(`/auth/token/refresh`, { userId });
  const userSettings = storage.get('currentUserSettings');
  storage.set('currentUserSettings', { ...userSettings && userSettings, ...{ impersonated: true } });
  return result;
};
/**
 * Logs in a user using SSO token.
 * API Endpoint: `/public/bstk/sso` via {@link oauthRequest.post}
 */
export const loginWithSso = async (ssoToken: string) => {
  const result = await oauthRequest.post('/public/bstk/sso', { ssoToken });
  storage.remove('currentUserSettings');
  storage.set('isNewSession', true);
  return result;
};

/**
 * Stops impersonating current user <br>
 * API Endpoint - `/auth/token/refresh` <br> via {@link oauthRequest.post}
 */
export const stopImpersonating = async () => {
  const result = await oauthRequest.post('/auth/token/refresh', { userId: null });
  const userSettings = storage.get('currentUserSettings');
  storage.set('currentUserSettings', { ...userSettings && userSettings, ...{ impersonated: false } });
  return result;
};

export default {
  getCurrent,
  login,
  loginWithRecoveryCode,
  logout,
  token,
  impersonate,
  stopImpersonating,
  resetPassword,
  forgotPassword,
  loginWithSso,
};
